/**
 * Mapa de errores global de Zod en español (es-CL, tuteo).
 *
 * Zod por defecto emite mensajes en inglés ("String must contain at least 1
 * character(s)", "Invalid email", etc.). Este módulo registra un `errorMap`
 * global con `z.setErrorMap` para que TODOS los formularios del cliente
 * muestren mensajes en español sin tener que poner `message` custom schema
 * por schema.
 *
 * Precedencia (Zod v3): un `message` explícito en el schema (ej.
 * `.min(1, 'Requerido')`, `.refine(fn, { message })`, `invalid_type_error`)
 * SIEMPRE gana sobre este map. El map solo aplica donde NO hay message custom.
 *
 * Se importa como side-effect en `main.tsx` (corre al bootstrap, antes de
 * cualquier validación). El map se consulta en tiempo de PARSE, no al definir
 * el schema, así que el orden relativo a los módulos de schemas no importa.
 *
 * Códigos no cubiertos caen a `ctx.defaultError` (inglés Zod) a propósito,
 * para no romper silenciosamente y dejar visible cualquier caso nuevo.
 */

import { type ZodErrorMap, ZodIssueCode, z } from 'zod';

const esErrorMap: ZodErrorMap = (issue, ctx) => {
  switch (issue.code) {
    case ZodIssueCode.invalid_type: {
      if (issue.expected === 'string') return { message: 'Este campo es obligatorio' };
      if (issue.expected === 'number') return { message: 'Ingresa un número' };
      if (issue.expected === 'boolean') return { message: 'Selecciona una opción' };
      if (issue.expected === 'date') return { message: 'Selecciona una fecha' };
      return { message: 'Tipo inválido' };
    }
    case ZodIssueCode.too_small: {
      if (issue.type === 'string') {
        return {
          message:
            issue.minimum === 1
              ? 'Este campo es obligatorio'
              : `Debe tener al menos ${issue.minimum} caracteres`,
        };
      }
      if (issue.type === 'number') {
        return {
          message: issue.inclusive
            ? `Debe ser mayor o igual a ${issue.minimum}`
            : `Debe ser mayor a ${issue.minimum}`,
        };
      }
      if (issue.type === 'array') {
        return {
          message:
            issue.minimum === 1
              ? 'Selecciona al menos 1 opción'
              : `Selecciona al menos ${issue.minimum} opciones`,
        };
      }
      return { message: ctx.defaultError };
    }
    case ZodIssueCode.too_big: {
      if (issue.type === 'string') {
        return { message: `Debe tener como máximo ${issue.maximum} caracteres` };
      }
      if (issue.type === 'number') {
        return {
          message: issue.inclusive
            ? `Debe ser menor o igual a ${issue.maximum}`
            : `Debe ser menor a ${issue.maximum}`,
        };
      }
      if (issue.type === 'array') {
        return {
          message: issue.maximum === 1 ? 'Máximo 1 opción' : `Máximo ${issue.maximum} opciones`,
        };
      }
      return { message: ctx.defaultError };
    }
    case ZodIssueCode.invalid_string: {
      if (typeof issue.validation === 'string') {
        switch (issue.validation) {
          case 'email':
            return { message: 'Email inválido' };
          case 'url':
            return { message: 'URL inválida' };
          case 'uuid':
            return { message: 'Identificador inválido' };
          case 'regex':
            return { message: 'Formato inválido' };
          default:
            return { message: ctx.defaultError };
        }
      }
      if (typeof issue.validation === 'object' && 'startsWith' in issue.validation) {
        return { message: `Debe comenzar con '${issue.validation.startsWith}'` };
      }
      return { message: ctx.defaultError };
    }
    case ZodIssueCode.invalid_enum_value:
      return { message: 'Opción inválida' };
    case ZodIssueCode.unrecognized_keys:
      return { message: `Hay campos no reconocidos: ${issue.keys.join(', ')}` };
    case ZodIssueCode.custom:
      return { message: 'Validación fallida' };
    default:
      return { message: ctx.defaultError };
  }
};

z.setErrorMap(esErrorMap);

export { esErrorMap };
